/**
 * overlayConfig.ts
 *
 * Centralised configuration for the JamCam fretboard overlay.
 * Stored in localStorage and shared via React context.
 */

import { NoteName, IntervalRole } from '../utils/musicTheory';
import { createPersistedConfigStore } from './persistedConfig';

/* ------------------------------------------------------------------ */
/*  Shared style primitives                                            */
/* ------------------------------------------------------------------ */

export type LineStyle = 'solid' | 'dashed' | 'dotted';

/** RGBA colour string, e.g. "rgba(255,255,255,0.6)" */
export type RGBAColor = string;

export interface StrokeStyle {
  color: RGBAColor;
  width: number;
  style: LineStyle;
}

export interface NoteVisualStyle {
  /** Fill colour (includes opacity via rgba) */
  color: RGBAColor;
  /** Radius in image-space pixels (0 = auto-sized from fret spacing) */
  radius: number;
  /** Border around the note circle */
  border: StrokeStyle;
  /** Whether to show the note name label inside the circle */
  showLabel: boolean;
  /** Label colour */
  labelColor: RGBAColor;
  /** Label font size (0 = auto) */
  labelSize: number;
}

/* ------------------------------------------------------------------ */
/*  Per-interval colour overrides                                      */
/* ------------------------------------------------------------------ */

/**
 * Each interval in the scale can have its own colour theme.
 * `null` means "use the category default" (ghost / scale).
 */
export type IntervalColorMap = Partial<Record<IntervalRole, RGBAColor>>;

/* ------------------------------------------------------------------ */
/*  Main overlay configuration                                         */
/* ------------------------------------------------------------------ */

export interface OverlayConfig {
  /* ── Camera ── */
  mirrorVideo: boolean;

  /* ── Global overlay ── */
  overlayOpacity: number; // 0-100
  overlayScale: number; // 70-130

  /* ── Fret lines ── */
  fretLines: {
    show: boolean;
    stroke: StrokeStyle;
  };

  /* ── String lines ── */
  stringLines: {
    show: boolean;
    stroke: StrokeStyle;
  };

  /* ── Ghost notes (all fretboard positions) ── */
  ghostNotes: {
    show: boolean;
    style: NoteVisualStyle;
  };

  /* ── Scale notes (only notes in the selected scale) ── */
  scaleNotes: {
    show: boolean;
    style: NoteVisualStyle;
    /** Per-interval colour overrides (root, 3rd, 5th, etc.) */
    intervalColors: IntervalColorMap;
  };

  /* ── Root note highlight ── */
  rootNotes: {
    show: boolean;
    style: NoteVisualStyle;
  };

  /* ── Fret-number flags (banner labels above the fretboard) ── */
  fretNumbers: {
    show: boolean;
    /** Banner / text colour */
    color: RGBAColor;
    /** Font size in image-space px (0 = auto) */
    size: number;
  };

  /* ── Interval-specific highlights (1st/3rd/5th overrides) ── */
  intervalHighlights: {
    show: boolean;
    /** Which intervals to highlight with their custom style */
    intervals: Partial<Record<IntervalRole, NoteVisualStyle>>;
  };

  /* ── Guided-note tier colours (used by guided-solo module) ── */
  guidedNotes: {
    chordTone: string;
    strongScale: string;
    passing: string;
    avoid: string;
  };

  /* ── Scale selection ── */
  scale: {
    root: NoteName;
    name: string; // key into SCALE_INTERVALS
  };

  /* ── Tuning ── */
  tuning: {
    preset: string; // 'Standard', 'Drop D', etc. or 'Custom'
    notes: NoteName[]; // per-string, low→high
  };

  /* ── Hand tracking ── */
  handTracking: {
    enabled: boolean;
    show: boolean;
    showSkeleton: boolean;
    showFingertips: boolean;
  };

  /* ── Audio input / pitch detection ── */
  audio: {
    /** 0-100. Higher = more sensitive (lower noise gate). */
    micSensitivity: number;
    /** 0-100. Lower = easier attack detection for softer/muted picking. */
    attackThreshold: number;
    /**
     * When true, sample the ambient noise floor for ~1.5s after the camera
     * starts and tighten the effective noise gate to ignore room noise
     * (fans, fridges, HVAC). The user's slider value is preserved as a
     * hard lower bound — calibration can only make the gate stricter.
     */
    autoCalibrate: boolean;
  };

  /* ── Played-note glow ring ── */
  playedNoteGlow: {
    show: boolean;
    fillColor: RGBAColor;
    borderColor: RGBAColor;
    borderWidth: number;
    /** 0–100 */
    opacity: number;
    /** Extra pixels added to the ghost-note radius */
    radiusOffset: number;
  };

  /* ── Miscellaneous ── */
  fretCount: number;
}

/* ------------------------------------------------------------------ */
/*  Defaults                                                           */
/* ------------------------------------------------------------------ */

const ghostStyle: NoteVisualStyle = {
  color: 'rgba(170, 175, 180, 0.20)',
  radius: 8,
  border: {
    color: 'rgba(170, 175, 180, 0.35)',
    width: 1,
    style: 'solid',
  },
  showLabel: false,
  labelColor: 'rgba(255, 255, 255, 0.95)',
  labelSize: 0,
};

const scaleStyle: NoteVisualStyle = {
  color: 'rgba(90, 200, 255, 0.65)',
  radius: 8,
  border: {
    color: 'rgba(90, 200, 255, 0.95)',
    width: 1.4,
    style: 'solid',
  },
  showLabel: false,
  labelColor: 'rgba(255, 255, 255, 0.95)',
  labelSize: 0,
};

const rootStyle: NoteVisualStyle = {
  color: 'rgba(90, 200, 255, 0.75)',
  radius: 8,
  border: {
    color: 'rgba(220, 40, 40, 0.95)',
    width: 4,
    style: 'solid',
  },
  showLabel: false,
  labelColor: 'rgba(255, 255, 255, 1)',
  labelSize: 0,
};

export const defaultOverlayConfig: OverlayConfig = {
  mirrorVideo: true,
  overlayOpacity: 78,
  overlayScale: 100,

  fretLines: {
    show: false,
    stroke: {
      color: 'rgba(98, 220, 180, 0.46)',
      width: 1.4,
      style: 'solid',
    },
  },

  stringLines: {
    show: false,
    stroke: {
      color: 'rgba(143, 199, 255, 0.24)',
      width: 1.1,
      style: 'solid',
    },
  },

  ghostNotes: {
    show: true,
    style: ghostStyle,
  },

  scaleNotes: {
    show: true,
    style: scaleStyle,
    intervalColors: {},
  },

  rootNotes: {
    show: false,
    style: rootStyle,
  },

  fretNumbers: {
    show: false,
    color: 'rgba(255, 255, 255, 0.95)',
    size: 0,
  },

  intervalHighlights: {
    show: false,
    intervals: {
      major3rd: {
        color: 'rgba(90, 200, 255, 0.75)',
        radius: 8,
        border: {
          color: 'rgba(255, 220, 0, 0.95)',
          width: 2.5,
          style: 'solid',
        },
        showLabel: false,
        labelColor: 'rgba(255, 255, 255, 0.9)',
        labelSize: 0,
      },
      minor3rd: {
        color: 'rgba(90, 200, 255, 0.75)',
        radius: 8,
        border: {
          color: 'rgba(255, 220, 0, 0.95)',
          width: 2.5,
          style: 'solid',
        },
        showLabel: false,
        labelColor: 'rgba(255, 255, 255, 0.9)',
        labelSize: 0,
      },
      perfect4th: {
        color: 'rgba(90, 200, 255, 0.75)',
        radius: 8,
        border: {
          color: 'rgba(90, 200, 255, 0.95)',
          width: 2.5,
          style: 'solid',
        },
        showLabel: false,
        labelColor: 'rgba(255, 255, 255, 0.9)',
        labelSize: 0,
      },
      perfect5th: {
        color: 'rgba(90, 200, 255, 0.75)',
        radius: 8,
        border: {
          color: 'rgba(30, 60, 160, 0.95)',
          width: 2.5,
          style: 'solid',
        },
        showLabel: false,
        labelColor: 'rgba(255, 255, 255, 0.9)',
        labelSize: 0,
      },
    },
  },

  guidedNotes: {
    chordTone: 'rgba(79, 195, 247, 0.82)',
    strongScale: 'rgba(76, 175, 80, 0.8)',
    passing: 'rgba(255, 214, 79, 0.82)',
    avoid: 'rgba(229, 115, 115, 0.68)',
  },

  handTracking: {
    enabled: true,
    show: false,
    showSkeleton: true,
    showFingertips: true,
  },

  audio: {
    micSensitivity: 90,
    attackThreshold: 50,
    autoCalibrate: true,
  },

  playedNoteGlow: {
    show: true,
    fillColor: 'rgba(255, 255, 255, 0.08)',
    borderColor: 'rgba(255, 255, 255, 0.95)',
    borderWidth: 3.5,
    opacity: 100,
    radiusOffset: 4,
  },

  scale: {
    root: 'C',
    name: 'Major Pentatonic',
  },

  tuning: {
    preset: 'Standard',
    notes: ['E', 'A', 'D', 'G', 'B', 'E'],
  },

  fretCount: 20,
};

/* ------------------------------------------------------------------ */
/*  Persistence                                                        */
/* ------------------------------------------------------------------ */

export const overlayConfigStore = createPersistedConfigStore(
  'jamcam-overlay-config-v6',
  defaultOverlayConfig
);
